import React from "react";
import { Send } from "lucide-react";
import { apiService } from "../../../services/index";

function containsEmail(email) {
  return /[^\s@]+@[^\s@]+\.[^\s@]+/.test(email || "");
}

function isValidPhone(phone) {
  return (phone || "").replace(/\D/g, "").length === 10;
}

function SendReferral({ result, isDetailPage, variant = "default" }) {
  const user = window.UW211?.CURRENT_USER || null;
  const programSiteId = result.program_site_id;
  const programName = result.name || result.program_name || "";
  const agencyName = result.agency_name || "";
  const isFeedbackLink = variant === "feedback-link";

  const [modalOpen, setModalOpen] = React.useState(false);
  const [emailSent, setEmailSent] = React.useState(false);
  const [sendError, setSendError] = React.useState("");

  const firstName = user?.first_name ? user.first_name.trim() : "";
  const lastName = user?.last_name ? user.last_name.trim() : "";

  const [name, setName] = React.useState(`${firstName} ${lastName}`.trim());
  const [email, setEmail] = React.useState(user?.email || "");
  const [phone, setPhone] = React.useState(user?.phone || "");
  const [message, setMessage] = React.useState(
    `Hello, are you accepting clients for ${programName} at this moment? Thank you.`
  );
  const [validEmail, setValidEmail] = React.useState(true);
  const [validPhone, setValidPhone] = React.useState(true);

  // Auto-open modal if returning from login with stored program_site_id
  React.useEffect(() => {
    const storedId = sessionStorage.getItem("program_site_id");
    if (user && storedId && String(storedId) === String(programSiteId)) {
      setModalOpen(true);
    }
  }, [user, programSiteId]);

  function handleClick() {
    if (user) {
      setModalOpen(true);
    } else {
      if (isDetailPage) {
        sessionStorage.setItem("lastSearchUrl", `/search/${programSiteId}`);
      }
      sessionStorage.setItem("program_site_id", programSiteId);
      window.location = "/referral/login";
    }
  }

  function handleClose() {
    sessionStorage.removeItem("program_site_id");
    setModalOpen(false);
    setEmailSent(false);
  }

  function handleSend(e) {
    e.preventDefault();
    setSendError("");
    apiService
      .post("/referral_emails", {
        for_email: email,
        for_phone: phone,
        for_name: name,
        message,
        program_site_id: programSiteId,
      })
      .then(() => setEmailSent(true))
      .catch(() => setSendError("Unable to send your request. Please try again."));
  }

  function validateEmail() {
    if (email && !containsEmail(email)) {
      setValidEmail(false);
    } else {
      setValidEmail(true);
    }
  }

  function validatePhone() {
    if (phone && !isValidPhone(phone)) {
      setValidPhone(false);
    } else {
      setValidPhone(true);
    }
  }

  return (
    <>
      {isDetailPage && !isFeedbackLink && (
        <div className="information-table">
          <h4 className="label referral-label">Service Requests</h4>
          <div>
            You can request services from this agency by sending a referral. When the agency responds, you will be
            notified by email and in your 211 account.
          </div>
        </div>
      )}

      <div
        onClick={handleClick}
        id="send-referral-btn"
        className={`referrals-row send-referral${isDetailPage && !isFeedbackLink ? " btn btn-primary more" : ""}${isFeedbackLink ? " search-detail-feedback-link" : ""}`}
        role="button"
        tabIndex={0}
      >
        <Send aria-hidden="true" size={isFeedbackLink ? 15 : 17} strokeWidth={isFeedbackLink ? 2 : 1.8} />
        <div className={isDetailPage || isFeedbackLink ? "send-referral-button-text" : "send-referral-button-text visually-hidden"}>Request Services</div>
      </div>

      {modalOpen && (
        <div className="modal-open">
          <div
            className="modal fade send-referral in"
            role="dialog"
            tabIndex={-1}
            style={{ zIndex: 9999, display: "block" }}
          >
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content referral-modal-content">
                <button
                  type="button"
                  aria-label="close"
                  className="referral-ico cancel-icon"
                  onClick={handleClose}
                />
                {!emailSent && (
                  <div className="modal-header">
                    <h2 className="referral-modal-header dark-blue-text">Request Services</h2>
                    <p className="referral-modal-sub-header">{agencyName}</p>
                  </div>
                )}
                <div className="content modal-body referral-input-box">
                  {emailSent ? (
                    <div className="success-message">
                      <h4 className="label dark-blue-text">Service Request Sent</h4>
                      <h3 className="dark-blue-text referral-header">
                        You will be notified by email when the agency responds.
                      </h3>
                      <p className="referral-confirmation">You can view all your service requests in your account.</p>
                      <a id="referral-confirmation-button" className="btn btn-default" href="/my211/referrals">
                        View your service requests
                      </a>
                    </div>
                  ) : (
                    <form className="referral-form">
                      <input
                        className="input name large-height gradient-background"
                        name="name"
                        placeholder="Full Name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                      />
                      <input
                        id={`email${!validEmail ? "-error-box" : ""}`}
                        className="email input large-height gradient-background"
                        name="email"
                        placeholder="E-mail"
                        value={email}
                        onChange={(e) => { setEmail(e.target.value); setValidEmail(true); }}
                        onBlur={validateEmail}
                      />
                      {!validEmail && <div className="input-error-text">Invalid E-mail</div>}
                      <input
                        id={`phone${!validPhone ? "-error-box" : ""}`}
                        className="input phone large-height gradient-background"
                        name="phone"
                        placeholder="Phone (optional)"
                        value={phone}
                        onChange={(e) => { setPhone(e.target.value); setValidPhone(true); }}
                        onBlur={validatePhone}
                      />
                      {!validPhone && <div className="input-error-text">Invalid Phone</div>}
                      <label>Add a Note (optional)</label>
                      <textarea
                        className="input message gradient-background"
                        name="message"
                        placeholder="Message"
                        rows="5"
                        wrap="hard"
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                      />
                      {sendError && <div className="text-danger" style={{ marginBottom: 10 }}>{sendError}</div>}
                      <button
                        className={`btn btn-primary more${!(email && name) ? " referral-disabled-button" : ""}`}
                        onClick={handleSend}
                        name="send"
                        disabled={!(email && name && validEmail && validPhone)}
                      >
                        Request Services
                      </button>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default SendReferral;
